// POM-6514 — completing a Product ID by hand once createCatalogDrug reports it could not
// generate one. A Product ID is PRX + drug code + 4-digit strength code + form code; the admin
// types only the middle (drug code + strength code). PRX and the form code are fixed here and
// enforced again by the backend, so neither can be overridden.

import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';

export const PRODUCT_ID_PREFIX = 'PRX';

export const MANUAL_PRODUCT_ID_CODE_LENGTH = 7;

export const MANUAL_PRODUCT_ID_FORMAT_HINT =
  'Enter a 3-character drug code followed by a 4-digit strength code (e.g. FLX0400)';

const PRODUCT_ID_GENERATION_FAILED_CODE = 'CATALOG_MISSING_PRODUCT_ID';

const PRODUCT_ID_ALREADY_EXISTS_CODE = 'PRODUCT_ID_ALREADY_EXIST_IN_CATALOG';

const manualProductIdCodeRegex = /^[A-Z][A-Z0-9]{2}\d{4}$/;

export const normalizeManualProductIdCode = (value) =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, MANUAL_PRODUCT_ID_CODE_LENGTH);

export const isManualProductIdCodeValid = (code) => manualProductIdCodeRegex.test(code);

export const buildManualProductId = (code, shortCode) => `${PRODUCT_ID_PREFIX}${code}${shortCode}`;

// The full Product ID a variant will be submitted with, or '' when it carries no complete manual
// one (it generates instead, or its code is not finished yet).
export const resolveManualProductId = ({ needsManualProductId, manualProductIdCode }, shortCode) =>
  needsManualProductId && isManualProductIdCodeValid(manualProductIdCode)
    ? buildManualProductId(manualProductIdCode, shortCode)
    : '';

// Why a variant's manual Product ID cannot be submitted as it is, or '' when it can: the catalog
// already has it, or an earlier variant in the same request uses it.
export const describeManualProductIdConflict = ({ productIds, takenProductIds, index }) => {
  const productId = productIds[index];

  if (!productId) return '';

  if (takenProductIds.includes(productId)) {
    return `${productId} already exists in the catalog. Enter a unique Product ID.`;
  }

  const firstIndex = productIds.indexOf(productId);

  return firstIndex < index
    ? `Variant ${firstIndex + 1} already uses ${productId}. Enter a unique Product ID.`
    : '';
};

// The variant's productId for the create payload -- only a complete code; submit stays blocked
// until every variant that needs one has one.
export const toManualProductIdInput = (code, shortCode) =>
  isManualProductIdCodeValid(code) ? { productId: buildManualProductId(code, shortCode) } : {};

const getErrorExtensions = (error) =>
  error?.response?.errors?.[0]?.extensions || error?.response?.extensions || {};

// errorData names the variants a generation failure was raised for ({ variantIndex }) or the
// Product IDs a duplicate was raised for ({ productId }). null when it names none -- a backend
// from before it did.
const pluckErrorData = ({ errorData }, key) =>
  Array.isArray(errorData) && errorData.length > 0 ? errorData.map((entry) => entry[key]) : null;

// Marks the variants that now need a manual Product ID -- all of them when the failure names
// none. A variant already marked stays marked, so a field the admin has typed into never
// disappears from under them.
export const flagVariantsNeedingManualProductId = (variants, failedVariantIndexes) =>
  variants.map((variant, index) => ({
    ...variant,
    needsManualProductId:
      Boolean(variant.needsManualProductId) ||
      failedVariantIndexes === null ||
      failedVariantIndexes.includes(index),
  }));

// Product ID problems from createCatalogDrug, reported in the dialog beside the fields they are
// about:
//   generation failed  -> onGenerationFailed({ failedVariantIndexes })
//   Product ID taken   -> onProductIdTaken(takenProductIds), null when the error names none
//   other refusal of a manual Product ID (INVALID_INPUT) -> snackbar
// Returns false for any other error, which the dialog reports its usual way.
export const handleManualProductIdError = ({
  error,
  showManualProductId,
  onGenerationFailed,
  onProductIdTaken,
  enqueueSnackbar,
}) => {
  const extensions = getErrorExtensions(error);
  const errorCode = extensions.code;

  if (errorCode === PRODUCT_ID_GENERATION_FAILED_CODE) {
    onGenerationFailed({ failedVariantIndexes: pluckErrorData(extensions, 'variantIndex') });
    return true;
  }

  if (!showManualProductId) return false;

  if (errorCode === PRODUCT_ID_ALREADY_EXISTS_CODE) {
    onProductIdTaken(pluckErrorData(extensions, 'productId'));
    return true;
  }

  if (errorCode === 'INVALID_INPUT') {
    enqueueSnackbar(getGraphQLErrorMessage(error), { variant: 'error' });
    return true;
  }

  return false;
};
